// @givmo/employer-module — one report tile (EP-WEB26, the Reports page redesign).
//
// The top of the Reports page is a grid of these: each real report — the year recap, the four CSV
// exports, the tax documents — is ONE tile that does ONE thing. Every tile names the report, says in
// a line what it is for, states its format and period, and carries a single action whose word and
// glyph say which kind of thing happens: DOWNLOAD a file, VIEW a section of this page, or OPEN another
// page. There is no disabled tile and no "coming soon": a tile exists because its action works.
//
// ══ THE WHOLE TILE IS THE BUTTON — WITHOUT NESTING CONTROLS ══════════════════════════════════════
//
// The action element (a `<button>` or a link) draws an `::after` that covers the tile, so a click
// anywhere on the tile runs it, while the DOM keeps exactly one interactive element per action.
// A control that must stay separately operable — the Tax lines tile's year picker — sits ABOVE that
// layer (`relative z-[1]`, inside the tile's own stacking context). A button inside a button, or a
// picker inside one, is invalid HTML and breaks the tab order (the module-square precedent in
// `@givmo/design-system`).
//
// The focus ring is drawn on the layer, so a keyboard user sees the whole tile lit, not a sliver of
// text in its corner: the design system's FOCUS_RING pairing (the ring plus a 2px `brand-500`
// outline), moved onto the pseudo-element.
//
// ══ TWO SHAPES, ONE DOM ═════════════════════════════════════════════════════════════════════════
//
// From `sm` up a tile is a card in the grid: the glyph beside the name, the line, then a footer with
// the format and period on the left and the action on the right. Below `sm` it is a full-width row:
// the glyph, the text column (name, line, format and period), and the action's glyph at the right
// edge. Both are the same elements placed by one three-column grid, so no control is ever rendered
// twice (a duplicated testid is a strict-mode failure for every Playwright selector that reads it).
// Every cell is placed by explicit `grid-row`/`grid-column` LINES: a `row-span-*` shorthand after a
// `row-start-*` resets the start to auto, and an auto-placed glyph drops to the tile's last row.

import type { MouseEvent, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon, cn, testIdProp } from '@givmo/design-system';
import { ArrowDown, ArrowRight, CircleAlert, Info, type LucideIcon } from 'lucide-react';
import type { EmployerModuleDoor } from '../../lib/module-paths';

/**
 * The layer every tile action draws over its tile, and its focus treatment. Exported so the three
 * action shapes (a download button, an in-page link, a router link) share one recipe.
 */
export const TILE_ACTION_CLASS = cn(
  'inline-flex shrink-0 items-center gap-6 rounded-md font-sans text-s13 font-w600 tracking-givmo',
  'outline-none',
  // The stretched hit area: the whole tile, with the tile's own radius.
  "after:absolute after:inset-0 after:rounded-xl after:content-['']",
  // The house focus indicator (FOCUS_RING), drawn around the WHOLE tile.
  'focus-visible:after:shadow-ring focus-visible:after:outline-solid focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-brand-500',
  'disabled:cursor-default',
);

export interface ReportTileProps {
  /** The tile root's testid. */
  testId: string;
  icon: LucideIcon;
  title: string;
  /** One line: what the report is for. */
  description: ReactNode;
  /** The format and the period ("CSV · 2026 to date"), or the Tax lines tile's year picker. */
  meta: ReactNode;
  /** The ONE action. It must carry {@link TILE_ACTION_CLASS} so it covers the tile. */
  action: ReactNode;
  /** A statement about the last attempt — an error that says what went wrong. */
  status?: ReactNode;
  /** A run in flight. */
  busy?: boolean;
}

/**
 * The tile's card: its border, radius, hover and — `isolate` — its own stacking context, so a control
 * raised above the action's layer (a year picker, the Tax documents tile's spreadsheet zone) can never
 * paint over the shell's sticky top bar when scrolled under it (a `z-10` there tied the bar's own
 * `z-10` and won on document order).
 */
export const TILE_SHELL_CLASS = cn(
  'group relative isolate min-w-0 rounded-xl border border-border-subtle bg-surface shadow-xs',
  'transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none',
  'hover:border-border hover:shadow-sm',
);

/**
 * How the last attempt at one export ended — the state a tile shows. `ready` is a file that finished
 * while the browser would have held it (another file was just saved and the person has not clicked
 * since, `csv-export.ts` § One save per interaction): the tile keeps it until one click saves it.
 */
export type ExportState =
  | { kind: 'idle' }
  | { kind: 'preparing' }
  | { kind: 'ready' }
  | { kind: 'saved' }
  | { kind: 'nothing'; message: string }
  | { kind: 'failed'; message: string };

/**
 * An export action's accessible name in each state. It carries the words the action shows ("Try
 * again", "Downloaded", "Ready — Save"), so a person who says what they see reaches the control
 * (WCAG 2.5.3, Label in Name), and names the file. `file` reads "the Impact CSV".
 */
export function exportActionName(kind: ExportState['kind'], file: string): string {
  switch (kind) {
    case 'preparing':
      return `Preparing… ${file}`;
    case 'ready':
      return `Ready — Save: ${file}`;
    case 'saved':
      return `Downloaded: ${file}`;
    case 'failed':
      return `Try again: download ${file}`;
    case 'idle':
    case 'nothing':
      return `Download ${file}`;
  }
}

/**
 * The line a tile keeps after an attempt that saved nothing: the reason a file did not arrive ("Not
 * downloaded." first), or that there was nothing to save. `null` for every other state.
 */
export function ExportStatusLine({
  state,
  id,
  testId,
}: {
  state: ExportState;
  /** The element id the action's `aria-describedby` points at. */
  id: string;
  testId: string;
}) {
  if (state.kind !== 'failed' && state.kind !== 'nothing') return null;
  const failed = state.kind === 'failed';
  return (
    <p
      id={id}
      className={cn(
        'flex items-start gap-6 font-sans text-s12 font-w500 leading-h15 tracking-normal',
        failed ? 'text-danger-700' : 'text-text-secondary',
      )}
      {...testIdProp(testId)}
    >
      <Icon icon={failed ? CircleAlert : Info} size={14} className="mt-2 shrink-0" aria-hidden />
      {/* The outcome first — no file was saved — then the reason. */}
      {failed ? `Not downloaded. ${state.message}` : state.message}
    </p>
  );
}

/**
 * An in-page VIEW: scrolls to a section of this page and moves focus there. A real link (`#anchor`), so
 * it copies and opens like one; the click is handled here rather than by a hash change, which would
 * hand the router a navigation for a page it is already on.
 */
export function ViewAction({
  anchor,
  label,
  testId,
}: {
  anchor: string;
  label: string;
  testId: string;
}) {
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(anchor);
    if (target === null) return;
    event.preventDefault();
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  };
  return (
    <a
      href={`#${anchor}`}
      onClick={onClick}
      aria-label={label}
      className={cn(TILE_ACTION_CLASS, 'text-brand-600 group-hover:text-brand-700')}
      {...testIdProp(testId)}
    >
      <Icon icon={ArrowDown} size={16} aria-hidden />
      <span className="max-sm:sr-only">View</span>
    </a>
  );
}

/** A door OUT of the module (the console's Organization › Tax documents). */
export function OpenAction({ door, testId }: { door: EmployerModuleDoor; testId: string }) {
  return (
    <Link
      to={door.to}
      search={door.search}
      aria-label={door.label}
      className={cn(TILE_ACTION_CLASS, 'text-brand-600 group-hover:text-brand-700')}
      {...testIdProp(testId)}
    >
      <span className="max-sm:sr-only">Open</span>
      <Icon icon={ArrowRight} size={16} aria-hidden />
    </Link>
  );
}

export function ReportTile({
  testId,
  icon,
  title,
  description,
  meta,
  action,
  status,
  busy = false,
}: ReportTileProps) {
  return (
    <li
      className={cn(
        TILE_SHELL_CLASS,
        'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-12',
        // Phone: a row — glyph · name, line, period · action.
        'gap-y-2 px-14 py-14',
        // From `sm`: a card — glyph and name, the line, then the footer.
        'sm:grid-rows-[auto_1fr_auto] sm:items-start sm:gap-y-10 sm:p-20',
      )}
      aria-busy={busy || undefined}
      {...testIdProp(testId)}
    >
      <span className="col-[1] row-[1/4] flex size-36 items-center justify-center self-start rounded-lg bg-brand-50 text-brand-600 sm:row-[1/2]">
        <Icon icon={icon} size={18} aria-hidden />
      </span>

      <p className="col-[2] row-[1/2] min-w-0 font-sans text-s15 font-w600 tracking-givmo text-text-strong sm:col-[2/4] sm:self-center">
        {title}
      </p>

      <div className="col-[2] row-[2/3] flex min-w-0 flex-col gap-8 sm:col-[1/4]">
        <p className="font-sans text-s13 leading-h15 tracking-normal text-text-tertiary">
          {description}
        </p>
        {status ?? null}
      </div>

      <div className="col-[2] row-[3/4] flex min-h-[20px] min-w-0 items-center gap-8 pt-2 font-sans text-s12 font-w500 tracking-normal text-text-secondary sm:col-[1/3] sm:self-end sm:pt-6">
        {meta}
      </div>

      <div className="col-[3] row-[1/4] flex items-center self-center sm:row-[3/4] sm:self-end sm:justify-self-end sm:pt-6">
        {action}
      </div>
    </li>
  );
}
